import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, rp, fmtDate, fmtTime, statusColor } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 text-xl font-semibold">{value}</div>
  </div>
);

export default function UnitProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [log, setLog] = useState({ log_type: "Maintenance", log_date: new Date().toISOString().slice(0, 10), description: "", cost: 0 });

  const load = useCallback(() => api.get(`/units/${id}/history`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const addLog = async () => {
    if (!log.description) return toast.error("Add a description");
    await api.post(`/units/${id}/logs`, { ...log, cost: Number(log.cost || 0) });
    setLog({ ...log, description: "", cost: 0 }); load(); toast.success("Log added");
  };
  const delLog = async (lid) => { await api.delete(`/units/${id}/logs/${lid}`); load(); };

  if (!d) return <div className="text-ink-soft">Loading…</div>;
  const u = d.unit;

  return (
    <div className="space-y-6" data-testid="admin-unit-profile">
      <div>
        <Link to="/admin/units" className="text-xs text-lm-red">← Rental Units</Link>
        <h1 className="font-display text-3xl tracking-tight" data-testid="up-code">{u.unit_code}</h1>
        <p className="text-sm text-ink-soft">{u.unit_name} · {u.model} · {u.tile_type} · {u.current_location || "—"}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Current Status" value={u.status} testid="up-status" />
        <Stat label="Total Rentals" value={d.total_rentals} testid="up-total-rentals" />
        <Stat label="Revenue Generated" value={rp(d.total_revenue)} />
        <Stat label="Next Booking" testid="up-next-booking"
          value={d.next_booking ? `${d.next_booking.rental_date} — ${d.next_booking.start_time}` : "—"} />
      </div>

      <section className="bg-white border border-black/10 rounded-lg p-5">
        <h2 className="text-base font-semibold">Rental History</h2>
        <div className="mt-3 divide-y divide-black/5 text-sm" data-testid="up-rental-history">
          {d.bookings.map((b) => (
            <Link key={b.id} to={`/admin/bookings/${b.id}`} className="flex items-center justify-between gap-3 py-2.5 hover:text-lm-red">
              <span>{fmtDate(b.rental_date)} · {b.start_time}–{fmtTime(b.end_datetime)} · {b.customer_name}</span>
              <span className="flex items-center gap-2 shrink-0">{b.booking_code}
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusColor(b.booking_status)}`}>{b.booking_status}</span></span>
            </Link>
          ))}
          {d.bookings.length === 0 && <div className="py-2 text-ink-soft">No rentals yet.</div>}
        </div>
      </section>

      <section className="bg-white border border-black/10 rounded-lg p-5">
        <h2 className="text-base font-semibold">Damage History</h2>
        <div className="mt-3 divide-y divide-black/5 text-sm" data-testid="up-damage-history">
          {d.damage_history.map((i) => {
            const issues = Object.entries(i.checklist || {}).filter(([, v]) => v === "issue").map(([k]) => k);
            return (
              <div key={i.id} className="py-2.5">
                <div className="flex justify-between gap-3">
                  <span>{i.inspection_date} · {i.issue_description || issues.join(", ") || "Issue reported"}</span>
                  <span className="shrink-0">Repair {rp(i.repair_cost)} · Deducted {rp(i.deposit_deduction)}</span>
                </div>
                {i.missing_item && <div className="text-xs text-ink-soft">Missing: {i.missing_item}</div>}
                {(i.photos || []).length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {i.photos.map((p, x) => <img key={`${i.id}-${x}`} src={p} alt="" className="w-16 h-16 object-cover rounded border border-black/10" />)}
                  </div>
                )}
              </div>
            );
          })}
          {d.damage_history.length === 0 && <div className="py-2 text-emerald-700">No damage recorded.</div>}
        </div>
      </section>

      <section className="bg-white border border-black/10 rounded-lg p-5">
        <h2 className="text-base font-semibold">Maintenance History</h2>
        <div className="mt-3 grid sm:grid-cols-5 gap-2">
          <select data-testid="up-log-type" className={sel} value={log.log_type} onChange={(e) => setLog({ ...log, log_type: e.target.value })}>
            {["Maintenance", "Repair", "Cleaning", "Upgrade", "Note"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <Input data-testid="up-log-date" type="date" value={log.log_date} onChange={(e) => setLog({ ...log, log_date: e.target.value })} />
          <div className="sm:col-span-2"><Textarea data-testid="up-log-desc" rows={1} placeholder="What was done?" value={log.description} onChange={(e) => setLog({ ...log, description: e.target.value })} /></div>
          <div className="flex gap-2">
            <Input type="number" placeholder="Cost" value={log.cost} onChange={(e) => setLog({ ...log, cost: e.target.value })} />
            <button data-testid="up-log-add" onClick={addLog} className="lm-btn-primary rounded-md px-4 text-sm font-semibold">Add</button>
          </div>
        </div>
        <div className="mt-3 divide-y divide-black/5 text-sm" data-testid="up-maintenance-history">
          {d.maintenance_logs.map((l) => (
            <div key={l.id} className="flex justify-between gap-3 py-2.5">
              <span>{l.log_date} · <span className="text-lm-red">{l.log_type}</span> · {l.description}</span>
              <span className="flex items-center gap-3 shrink-0">{rp(l.cost)}
                <button onClick={() => delLog(l.id)} className="text-ink-soft hover:text-lm-red text-xs">Delete</button></span>
            </div>
          ))}
          {d.maintenance_logs.length === 0 && <div className="py-2 text-ink-soft">No maintenance logged.</div>}
        </div>
        {u.notes && <p className="mt-4 text-sm text-ink-soft">Notes: {u.notes}</p>}
      </section>
    </div>
  );
}
